/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import {
    OSD_MOVEMENT_TIME,
    OSD_STILL_TIME,
    PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_720P,
    PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_1080P,
    PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_720P,
    PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_1080P,
} from '../../../config';
import { DeviceModel } from '../../../core/device/DeviceModel';
import { ServicesClientImpl } from '../../../core/interactors/ServicesClientImpl';
import { RootState, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';
import { HighlightTexts } from '../../OverlayDialog/common/HighlightTexts';
import { OsdProps } from './OsdProps';
import { OsdTypes } from './OSDTypes';

import './Osd.scss';

const Osd: FunctionComponent<OsdProps> = (props): JSX.Element => {
    const { channel } = useTivoSelector((state: RootState) => state.videoPlayer);
    const isTrickplayMenuActive = useTivoSelector(
        (state: RootState) => state.infoBanner.isTrickplayMenuActive,
    );
    const isStandardInfoActive = useTivoSelector(
        (state: RootState) => state.infoBanner.isStandardInfoActive,
    );
    const isFullInfoActive = useTivoSelector(
        (state: RootState) => state.infoBanner.isFullInfoActive,
    );
    const { showOneLineGuide: isOneLineGuideActive } = useTivoSelector(
        (state: RootState) => state.oneLineGuide,
    );
    const visibilityState = useTivoSelector(
        (state: RootState) => state.visibilityEvents.visibilityState,
    );
    const { message, code, type = OsdTypes.DEFAULT } = props;
    const [osdMovement, setOsdMovement] = useState({});
    const osdRef = useRef<HTMLDivElement>(null);
    const osdContainerRef = useRef<HTMLDivElement>(null);
    let interval;

    const primaryBrandingImageUrl = useRef('');

    useEffect(() => {
        const service = new ServicesClientImpl();

        primaryBrandingImageUrl.current = DeviceModel.is1080P()
            ? service.getPartnerBrandingImageUrl(
                  PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_1080P,
                  PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_1080P,
              )
            : service.getPartnerBrandingImageUrl(
                  PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_720P,
                  PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_720P,
              );
    }, []);

    const updateOsdPosition = () => {
        if (!osdContainerRef.current || !osdRef.current) return;
        const osdContainerHeight = osdContainerRef.current.offsetHeight;
        const osdContainerWidth = osdContainerRef.current.offsetWidth;
        const osdWidth = osdRef.current.offsetWidth;
        const osdHeight = osdRef.current.offsetHeight;

        const topMovement = Math.random() * (osdContainerHeight - osdHeight);
        const leftMovement = Math.random() * (osdContainerWidth - osdWidth);
        setOsdMovement({ top: `${topMovement}px`, left: `${leftMovement}px` });
    };

    useEffect(() => {
        setOsdMovement({ top: 'auto', left: 'auto' });
        const timer = setTimeout(() => {
            interval = setInterval(updateOsdPosition, OSD_MOVEMENT_TIME);
        }, OSD_STILL_TIME);
        return () => {
            clearTimeout(timer);
            clearInterval(interval);
        };
    }, [channel, visibilityState]);

    useEffect(() => {
        setOsdMovement({ top: 'auto', left: 'auto' });
    }, [isFullInfoActive, isStandardInfoActive, isTrickplayMenuActive, isOneLineGuideActive]);

    const osd = (
        <div className={`osd-container  ${type}`} ref={osdContainerRef} data-testid="osd-container">
            <div className="osd" style={osdMovement} ref={osdRef}>
                <ImageLoader
                    src={primaryBrandingImageUrl.current}
                    imageType={ImageLoaderTypes.OSD_BRANDING_ICON}
                    data-testid="osd-branding-icon"
                />
                <div className="osd-message" data-testid="osd-message">
                    <HighlightTexts text={message} />
                </div>
                {code ? (
                    <div className="osd-message-code" data-testid="osd-message-code">
                        {code}
                    </div>
                ) : null}
            </div>
        </div>
    );

    return osd;
};

Osd.propTypes = {
    message: PropTypes.string.isRequired,
    code: PropTypes.string,
    type: PropTypes.string,
};

export default Osd;
